import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { IPlan } from "~/types/billing";
import blogCover from "~/assets/onboarding/blog-cover.webp";
import onModel from "~/assets/onboarding/on-model.webp";
import supplierPhoto from "~/assets/onboarding/supplier-photo.webp";
import styles from "./OnboardingFlow.module.css";
import {
  CATEGORY_DETAILS,
  MORE_TOOLS,
  ONBOARDING_CATEGORIES,
  TOOL_ICONS,
  type OnboardingCategory,
  type PlanDetails,
  getPlanDetails,
  orderedCategories,
  toggleCategory,
} from "./onboarding-utils";
import { OnboardingIcon } from "./OnboardingIcon";
import { useOnboardingTracking } from "./useOnboardingTracking";

const TOTAL_STEPS = 3;

type Step = "categories" | "preview" | "studio" | "welcome";

type OnboardingFlowProps = {
  // Set after payment: the tools picked before checkout, for the welcome
  // screen's quick starts.
  welcomeCategories: OnboardingCategory[] | null;
  // Active plans, for the plan badges and what Starter includes.
  plans: IPlan[] | undefined;
  // "Unlock Smartli" or "Skip": open the existing checkout.
  onCheckout: (categories: OnboardingCategory[]) => void;
  // The welcome screen is dismissed: show the app.
  onFinish: () => void;
};

export function OnboardingFlow({
  welcomeCategories,
  plans,
  onCheckout,
  onFinish,
}: OnboardingFlowProps) {
  const track = useOnboardingTracking();
  const [step, setStep] = useState<Step>(
    welcomeCategories ? "welcome" : "categories"
  );
  // The design starts with every tool picked.
  const [categories, setCategories] = useState<OnboardingCategory[]>([
    ...ONBOARDING_CATEGORIES,
  ]);
  const planDetails = useMemo(() => getPlanDetails(plans), [plans]);

  useEffect(() => {
    track("onboarding_step_view", { step });
    // Once per step, not each time the tracker is recreated.
  }, [step]);

  useEffect(() => {
    if (welcomeCategories) setStep("welcome");
  }, [welcomeCategories]);

  const skip = () => {
    track("onboarding_skip", { step });
    onCheckout(categories);
  };

  const unlock = () => {
    track("onboarding_unlock_click", { tools: categories.join(",") });
    onCheckout(categories);
  };

  return (
    <div className={styles.container}>
      {step === "categories" && (
        <CategoriesStep
          categories={categories}
          planDetails={planDetails}
          onToggle={(category) =>
            setCategories((selected) => toggleCategory(selected, category))
          }
          onNext={() => setStep("preview")}
          onSkip={skip}
        />
      )}
      {step === "preview" && (
        <PreviewStep
          categories={orderedCategories(categories)}
          planDetails={planDetails}
          onBack={() => setStep("categories")}
          onNext={() => setStep("studio")}
          onSkip={skip}
        />
      )}
      {step === "studio" && (
        <StudioStep
          categories={orderedCategories(categories)}
          planDetails={planDetails}
          onBack={() => setStep("preview")}
          onUnlock={unlock}
          onSkip={skip}
        />
      )}
      {step === "welcome" && (
        <WelcomeStep
          categories={orderedCategories(welcomeCategories ?? [])}
          planDetails={planDetails}
          onDashboard={onFinish}
        />
      )}
    </div>
  );
}

function StepHeader({
  current,
  title,
  subtitle,
}: {
  current: number;
  title: string;
  subtitle: string;
}) {
  const { t } = useTranslation();
  return (
    <div className={styles.header}>
      <div className={styles.stepIndicator}>
        <span className={styles.stepLabel}>
          {String(t("onboarding_v2.step_of", { current, total: TOTAL_STEPS }))}
        </span>
        <div className={styles.stepBars} aria-hidden="true">
          {Array.from({ length: TOTAL_STEPS }, (_, index) => (
            <span
              key={index}
              className={`${styles.stepBar} ${index < current ? styles.stepBarActive : ""}`}
            />
          ))}
        </div>
      </div>
      <h2 className={styles.title}>{title}</h2>
      <p className={styles.subtitle}>{subtitle}</p>
    </div>
  );
}

function PrimaryButton({
  children,
  onClick,
  disabled,
  arrow,
}: {
  children: string;
  onClick: () => void;
  disabled?: boolean;
  arrow?: boolean;
}) {
  return (
    <button
      type="button"
      className={styles.primaryButton}
      onClick={onClick}
      disabled={disabled}>
      {children}
      {arrow && <OnboardingIcon name="arrow-right" />}
    </button>
  );
}

function TextButton({
  children,
  onClick,
  backArrow,
}: {
  children: string;
  onClick: () => void;
  backArrow?: boolean;
}) {
  return (
    <button type="button" className={styles.textButton} onClick={onClick}>
      {backArrow && <OnboardingIcon name="arrow-left" />}
      {children}
    </button>
  );
}

function Footer({
  onBack,
  onSkip,
  skipLabel,
  children,
}: {
  onBack?: () => void;
  onSkip: () => void;
  skipLabel: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className={styles.footer}>
      {onBack ? (
        <TextButton backArrow onClick={onBack}>
          {String(t("onboarding_v2.back"))}
        </TextButton>
      ) : (
        <TextButton onClick={onSkip}>{skipLabel}</TextButton>
      )}
      <div className={styles.footerEnd}>
        {onBack && <TextButton onClick={onSkip}>{skipLabel}</TextButton>}
        {children}
      </div>
    </div>
  );
}

// The "Pro" badge: the plan AI Blogs starts on.
function PlanBadge({ name }: { name: string }) {
  return <span className={styles.planBadge}>{name}</span>;
}

function CategoryIcon({
  category,
  large,
}: {
  category: OnboardingCategory;
  large?: boolean;
}) {
  return (
    <span className={`${styles.previewIcon} ${large ? styles.iconLarge : ""}`}>
      <OnboardingIcon
        name={CATEGORY_DETAILS[category].icon}
        size={large ? 20 : 16}
      />
    </span>
  );
}

function ToolChips({
  label,
  categories = [],
  planDetails,
}: {
  label: string;
  // Main tools that weren't picked, listed before the other tools.
  categories?: OnboardingCategory[];
  planDetails: PlanDetails;
}) {
  const { t } = useTranslation();
  return (
    <div className={styles.toolsBox}>
      <span className={styles.sectionHeading}>{label}</span>
      <div className={styles.chips}>
        {categories.map((category) => (
          <span key={category} className={styles.chip}>
            <OnboardingIcon name={CATEGORY_DETAILS[category].icon} />
            {String(t(`onboarding_v2.categories.${category}.title`))}
            {category === "ai_blogs" && (
              <PlanBadge name={planDetails.blogsPlan} />
            )}
          </span>
        ))}
        {MORE_TOOLS.map((tool) => (
          <span key={tool} className={styles.chip}>
            <OnboardingIcon name={TOOL_ICONS[tool]} />
            {String(t(`onboarding_v2.tools.${tool}`))}
          </span>
        ))}
      </div>
    </div>
  );
}

// ---------- step 1: what Smartli should create ----------

function DescriptionVisual() {
  const { t } = useTranslation();
  return (
    <div className={styles.docVisual} aria-hidden="true">
      <div className={styles.docHeader}>
        <span>
          {String(t("onboarding_v2.preview.product_descriptions.title"))}
        </span>
        <span className={`${styles.pill} ${styles.pillSuccess}`}>
          {String(t("onboarding_v2.preview.seo_score", { score: 94 }))}
        </span>
      </div>
      <div className={styles.docBody}>
        <span className={`${styles.docLine} ${styles.docLineStrong}`} />
        <span className={styles.docLine} style={{ width: "72%" }} />
        <span className={styles.docLine} style={{ width: "68%" }} />
        <span className={styles.docLine} style={{ width: "50%" }} />
        <span className={styles.chips}>
          <span className={styles.keyword}>puffer tote</span>
          <span className={styles.keyword}>quilted bag</span>
        </span>
      </div>
    </div>
  );
}

function CategoriesStep({
  categories,
  planDetails,
  onToggle,
  onNext,
  onSkip,
}: {
  categories: OnboardingCategory[];
  planDetails: PlanDetails;
  onToggle: (category: OnboardingCategory) => void;
  onNext: () => void;
  onSkip: () => void;
}) {
  const { t } = useTranslation();
  const visuals: Record<OnboardingCategory, ReactNode> = {
    ai_photoshoot: (
      <img className={styles.categoryImage} src={onModel} alt="" />
    ),
    product_descriptions: <DescriptionVisual />,
    ai_blogs: <img className={styles.categoryImage} src={blogCover} alt="" />,
  };

  return (
    <div className={styles.card}>
      <StepHeader
        current={1}
        title={String(t("onboarding_v2.categories.title"))}
        subtitle={String(t("onboarding_v2.categories.subtitle"))}
      />

      <div className={styles.categoryGrid}>
        {ONBOARDING_CATEGORIES.map((category) => {
          const selected = categories.includes(category);
          return (
            <button
              key={category}
              type="button"
              aria-pressed={selected}
              className={`${styles.tile} ${styles.categoryTile} ${selected ? styles.tileSelected : ""}`}
              onClick={() => onToggle(category)}>
              <span className={styles.categoryVisual}>
                {visuals[category]}
                <span className={styles.checkbox} aria-hidden="true">
                  {selected && <OnboardingIcon name="check" size={12} />}
                </span>
              </span>
              <span className={styles.categoryName}>
                <CategoryIcon category={category} />
                {String(t(`onboarding_v2.categories.${category}.title`))}
                {category === "ai_blogs" && (
                  <PlanBadge name={planDetails.blogsPlan} />
                )}
              </span>
              <span className={styles.categoryDescription}>
                {String(t(`onboarding_v2.categories.${category}.description`))}
              </span>
            </button>
          );
        })}
      </div>

      <ToolChips
        label={String(t("onboarding_v2.categories.more_tools"))}
        planDetails={planDetails}
      />

      <div className={styles.divider} />

      <Footer onSkip={onSkip} skipLabel={String(t("onboarding_v2.skip_setup"))}>
        {categories.length > 0 && (
          <span className={styles.selectedCount}>
            {String(
              t("onboarding_v2.categories.selected", {
                count: categories.length,
              })
            )}
          </span>
        )}
        <PrimaryButton
          arrow
          disabled={categories.length === 0}
          onClick={onNext}>
          {String(t("onboarding_v2.next"))}
        </PrimaryButton>
      </Footer>
    </div>
  );
}

// ---------- step 2: an example product, with the design's data ----------

function PreviewCard({
  category,
  planDetails,
  children,
}: {
  category: OnboardingCategory;
  planDetails: PlanDetails;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className={styles.previewCard}>
      <div className={styles.previewHeader}>
        <CategoryIcon category={category} />
        <span className={styles.previewTitle}>
          {String(t(`onboarding_v2.preview.${category}.title`))}
        </span>
        {category === "ai_blogs" && <PlanBadge name={planDetails.blogsPlan} />}
      </div>
      {children}
    </div>
  );
}

function PhotoshootPreview({ planDetails }: { planDetails: PlanDetails }) {
  const { t } = useTranslation();
  return (
    <PreviewCard category="ai_photoshoot" planDetails={planDetails}>
      <div className={styles.photoPair}>
        <figure className={styles.photo}>
          <img src={supplierPhoto} alt="" />
          <figcaption className={styles.rowSub}>
            {String(t("onboarding_v2.preview.ai_photoshoot.supplier_photo"))}
          </figcaption>
        </figure>
        <figure className={styles.photo}>
          <span className={styles.photoFrame}>
            <img src={onModel} alt="" />
            <span className={styles.aiBadge}>
              <OnboardingIcon name="sparkles" />
              {String(t("onboarding_v2.preview.ai"))}
            </span>
          </span>
          <figcaption className={styles.photoCaptionStrong}>
            {String(t("onboarding_v2.preview.ai_photoshoot.on_model"))}
          </figcaption>
        </figure>
      </div>
      <p className={styles.quote}>
        {String(t("onboarding_v2.preview.ai_photoshoot.note"))}
      </p>
    </PreviewCard>
  );
}

function DescriptionsPreview({ planDetails }: { planDetails: PlanDetails }) {
  const { t } = useTranslation();
  return (
    <PreviewCard category="product_descriptions" planDetails={planDetails}>
      <div className={styles.beforeBox}>
        <div className={styles.scoreRow}>
          <span className={styles.sectionHeading}>
            {String(t("onboarding_v2.preview.product_descriptions.before"))}
          </span>
          <span className={`${styles.pill} ${styles.pillDanger}`}>
            {String(t("onboarding_v2.preview.seo_score", { score: 38 }))}
          </span>
        </div>
        <p className={styles.quote}>
          Puffer Tote Bag. Nice bag, good quality. Buy now!
        </p>
      </div>
      <div className={styles.afterBox}>
        <div className={styles.scoreRow}>
          <span className={styles.afterLabel}>
            <OnboardingIcon name="sparkles" />
            {String(t("onboarding_v2.preview.product_descriptions.after"))}
          </span>
          <span className={`${styles.pill} ${styles.pillSuccess}`}>
            {String(t("onboarding_v2.preview.seo_score", { score: 94 }))}
          </span>
        </div>
        <span className={styles.afterTitle}>
          Quilted Puffer Tote Bag – Lightweight Padded Shoulder Bag
        </span>
        <p className={styles.quote}>
          Stay organised in style. The padded shell protects your essentials and
          the roomy inside fits a 13" laptop. An easy everyday bag for work,
          travel and weekends.
        </p>
      </div>
      <span className={styles.rowSub}>
        {String(t("onboarding_v2.preview.product_descriptions.keywords"))}
      </span>
      <div className={styles.chips}>
        {["puffer tote bag", "quilted shoulder bag", "padded handbag"].map(
          (keyword) => (
            <span key={keyword} className={styles.chip}>
              {keyword}
            </span>
          )
        )}
      </div>
    </PreviewCard>
  );
}

function BlogPreview({ planDetails }: { planDetails: PlanDetails }) {
  const { t } = useTranslation();
  return (
    <PreviewCard category="ai_blogs" planDetails={planDetails}>
      <img className={styles.blogImage} src={blogCover} alt="" />
      <span className={styles.blogTitle}>
        5 ways to style a puffer tote this autumn
      </span>
      <p className={styles.quote}>
        From office days to weekend trips, the quilted puffer tote is the one
        bag that does it all. Here’s how to wear it, what to pair it with and
        why it’s this season’s favourite…
      </p>
      <span className={styles.blogMeta}>
        {String(t("onboarding_v2.preview.ai_blogs.meta"))}
      </span>
    </PreviewCard>
  );
}

const PREVIEWS: Record<
  OnboardingCategory,
  (props: { planDetails: PlanDetails }) => JSX.Element
> = {
  ai_photoshoot: PhotoshootPreview,
  product_descriptions: DescriptionsPreview,
  ai_blogs: BlogPreview,
};

function PreviewStep({
  categories,
  planDetails,
  onBack,
  onNext,
  onSkip,
}: {
  categories: OnboardingCategory[];
  planDetails: PlanDetails;
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className={styles.card}>
      <StepHeader
        current={2}
        title={String(t("onboarding_v2.preview.title"))}
        subtitle={String(t("onboarding_v2.preview.subtitle"))}
      />

      <div className={styles.previewGrid}>
        {categories.map((category) => {
          const Preview = PREVIEWS[category];
          return <Preview key={category} planDetails={planDetails} />;
        })}
      </div>

      <div className={styles.divider} />

      <Footer
        onBack={onBack}
        onSkip={onSkip}
        skipLabel={String(t("onboarding_v2.skip_for_now"))}>
        <PrimaryButton arrow onClick={onNext}>
          {String(t("onboarding_v2.next"))}
        </PrimaryButton>
      </Footer>
    </div>
  );
}

// ---------- step 3: the studio ----------

function StudioStep({
  categories,
  planDetails,
  onBack,
  onUnlock,
  onSkip,
}: {
  categories: OnboardingCategory[];
  planDetails: PlanDetails;
  onBack: () => void;
  onUnlock: () => void;
  onSkip: () => void;
}) {
  const { t } = useTranslation();
  const notPicked = ONBOARDING_CATEGORIES.filter(
    (category) => !categories.includes(category)
  );

  return (
    <div className={styles.card}>
      <StepHeader
        current={3}
        title={String(t("onboarding_v2.studio.title"))}
        subtitle={String(t("onboarding_v2.studio.subtitle"))}
      />

      <div className={styles.pickGrid}>
        {categories.map((category) => (
          <div key={category} className={styles.pickCard}>
            <CategoryIcon category={category} large />
            <span className={styles.rowText}>
              <span className={styles.pickTitle}>
                {String(t(`onboarding_v2.categories.${category}.title`))}
                {category === "ai_blogs" && (
                  <PlanBadge name={planDetails.blogsPlan} />
                )}
              </span>
              <span className={styles.pickText}>
                {String(
                  t(`onboarding_v2.studio.${category}`, {
                    count: planDetails.starterDescriptions,
                    plan: planDetails.starterPlan,
                  })
                )}
              </span>
            </span>
            <span className={styles.pickCheck}>
              <OnboardingIcon name="check" size={16} />
            </span>
          </div>
        ))}
      </div>

      <ToolChips
        label={String(t("onboarding_v2.studio.also_included"))}
        categories={notPicked}
        planDetails={planDetails}
      />

      <p className={styles.planNote}>
        {String(
          t("onboarding_v2.studio.plan_note", {
            blogsPlan: planDetails.blogsPlan,
            adsWriterPlan: planDetails.adsWriterPlan,
          })
        )}
      </p>

      <div className={styles.divider} />

      <Footer
        onBack={onBack}
        onSkip={onSkip}
        skipLabel={String(t("onboarding_v2.skip_for_now"))}>
        <PrimaryButton arrow onClick={onUnlock}>
          {String(t("onboarding_v2.studio.unlock"))}
        </PrimaryButton>
      </Footer>
    </div>
  );
}

// ---------- after payment: welcome and quick starts ----------

function WelcomeStep({
  categories,
  planDetails,
  onDashboard,
}: {
  categories: OnboardingCategory[];
  planDetails: PlanDetails;
  onDashboard: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className={`${styles.card} ${styles.success}`}>
      <div className={`${styles.successIcon} ${styles.welcomeIcon}`}>
        <OnboardingIcon name="check" size={22} />
      </div>
      <div className={styles.successText}>
        <h2 className={styles.title}>
          {String(t("onboarding_v2.welcome.title"))}
        </h2>
        <p className={styles.successSubtitle}>
          {String(t("onboarding_v2.welcome.subtitle"))}
        </p>
      </div>

      <div className={styles.quickGrid}>
        {categories.map((category) => (
          <div key={category} className={styles.quickCard}>
            <span className={styles.quickIconRow}>
              <CategoryIcon category={category} large />
              {category === "ai_blogs" && (
                <PlanBadge name={planDetails.blogsPlan} />
              )}
            </span>
            <span className={styles.quickTitle}>
              {String(t(`onboarding_v2.welcome.${category}.title`))}
            </span>
            <p className={styles.quickText}>
              {String(t(`onboarding_v2.welcome.${category}.description`))}
            </p>
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => {
                onDashboard();
                navigate(CATEGORY_DETAILS[category].path);
              }}>
              {String(t(`onboarding_v2.welcome.${category}.open`))}
              <OnboardingIcon name="arrow-right" size={12} />
            </button>
          </div>
        ))}
      </div>

      <div className={styles.divider} />

      <div className={`${styles.footer} ${styles.footerEndOnly}`}>
        <PrimaryButton arrow onClick={onDashboard}>
          {String(t("onboarding_v2.welcome.go_to_dashboard"))}
        </PrimaryButton>
      </div>
    </div>
  );
}
